import React, { useState, useCallback, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { adsManagementSchema } from "../../schemas/adsManagementSchema";

import { FileText, Megaphone, PanelTop, XCircle } from "lucide-react";
import { useFileUpload } from "../../hooks/useFileUplode";
import ImageCropModal from "../../components/ImageCropModal";

export default function AdsManagementForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    control,
  } = useForm({
    resolver: yupResolver(adsManagementSchema),
    defaultValues: { ...defaultValues, mode },
  });
  const { fileUpload, fileDelete } = useFileUpload();
  const [imageSrc, setImageSrc] = useState(null);
  const [croppedImage, setCroppedImage] = useState(defaultValues.image || "");
  const [modalOpen, setModalOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleCropCancel = () => {
    setModalOpen(false);
    setImageSrc(null);
    fileInputRef.current.value = ""; // Clear file input
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Title */}
      <div>
        <label className="block text-sm font-medium mb-1">Title</label>
        <div className="relative">
          <PanelTop className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 mr-5" />
          <input
            {...register("title")}
            readOnly={mode === "view"}
            placeholder="Enter Ad title"
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.title && (
          <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>
        )}
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium mb-1">Description</label>
        <textarea
          {...register("description")}
          readOnly={mode === "view"}
          placeholder="Enter description"
          className="pl-3 pr-3 py-2 border rounded w-full"
          rows={3}
        />
        {errors.description && (
          <p className="text-red-500 text-xs mt-1">
            {errors.description.message}
          </p>
        )}
      </div>

      {/* Price */}
      <div>
        <label className="block text-sm font-medium mb-1">Loyalty Points</label>
        <input
          type="number"
          step="0.01"
          {...register("loyalty_points")}
          readOnly={mode === "view"}
          placeholder="Enter price"
          className="pl-3 pr-3 py-2 border rounded w-full"
        />
        {errors.loyalty_points && (
          <p className="text-red-500 text-xs mt-1">
            {errors.loyalty_points.message}
          </p>
        )}
      </div>

      {/* Image Upload and Crop */}
      <div>
        <label className="block text-sm font-medium mb-1">
          {mode === "view" ? "Image" : "Upload and Crop Image"}
        </label>

        {croppedImage && (
          <div className="mb-2 relative inline-block border border-gray-300 p-2 rounded-md">
            <img
              src={croppedImage}
              alt="Cropped"
              className="w-[468] h-[60] object-cover rounded-md"
            />
            {mode !== "view" && (
              <XCircle
                size={20}
                onClick={() => {
                  fileDelete(watch("image"));
                  setCroppedImage("");
                  setValue("image", "");
                }}
                className="absolute -top-1 -right-1 cursor-pointer text-red-500 bg-white rounded-full"
              />
            )}
          </div>
        )}

        {mode !== "view" && (
          <>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setImageSrc(reader.result);
                    setModalOpen(true);
                  };
                  reader.readAsDataURL(file);
                }
              }}
              ref={fileInputRef}
              disabled={mode === "view"}
              className="w-full border rounded py-2 px-3"
            />
            <Controller name="image" control={control} render={() => null} />
          </>
        )}

        {!croppedImage && mode === "view" && (
          <p className="text-sm text-gray-500">No image uploaded</p>
        )}
      </div>

      {/* Crop Modal */}
      <ImageCropModal
        open={modalOpen}
        imageSrc={imageSrc}
        onClose={() => handleCropCancel()}
        onCropDone={async (croppedImageBlob) => {
          const formData = new FormData();
          formData.append("document", croppedImageBlob, "cropped-image.jpg");
          const response = await fileUpload(formData);
          const uploadedUrl = response?.url || response?.data?.url || "";
          setCroppedImage(uploadedUrl);
          setValue("image", uploadedUrl);
          setModalOpen(false);
        }}
      />

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border rounded text-gray-700"
        >
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button
            type="submit"
            disabled={submissionLoading}
            className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2"
          >
            {submissionLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
                  ></path>
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Ad"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
